import { JSONSchemaType } from "ajv";
import { type $tr } from "@/i18n";
import { Awaitable } from "@vueuse/core";

export type DefaultProps = {
    $t: typeof $tr;
};

export interface DefaultFunction<T> {
    (props: DefaultProps): T;
}

export interface NameMapper<T> {
    (props: DefaultProps, val: T): string;
}

export interface IconMapper<T> {
    (props: DefaultProps, val: T): string;
}

export type StructurableEditorBoolean = {
    name?: NameMapper<boolean>;
    icon?: IconMapper<boolean>;
} & (
    | {
          kind: "checkbox";
      }
    | {
          kind: "switch";
      }
);

export type StructurableEditorObjectArray<T> = {
    kind: "list-editor";
    fieldset?: boolean;
    using: ArrayStructurableEditor<T>;
    factory: (props: DefaultProps, existing: T[]) => Awaitable<T | null>;
    name?: NameMapper<T[]>;
    addName?: NameMapper<T>;
    icon?: IconMapper<T>;
};

export type StructurableEditorStringArray = {
    name?: NameMapper<string[]>;
    icon?: IconMapper<string[]>;
} & (
    | {
          kind: "list-delimited";
          delimiter: string;
      }
    | {
          kind: "user-picker";
      }
);

export type StructurableEditorString = {
    name?: NameMapper<string>;
    icon?: IconMapper<string>;
} & (
    | {
          kind: "field";
      }
    | {
          kind: "select";
          enumName?: NameMapper<string>;
      }
);

export type StructurableEditorNumber = {
    kind: "number-field";
    name?: NameMapper<number>;
    icon?: IconMapper<number>;
};

export type StructurableEditorCommon = {
    kind: "hidden";
    name?: NameMapper<unknown>;
    icon?: IconMapper<unknown>;
};

export type DistributiveStructurableEditor<T> = T extends any
    ? StructurableEditor<T>
    : never;

export type ArrayStructurableEditor<T> = (
    item: T extends never ? never : T,
) => DistributiveStructurableEditor<T>;

export type StructurableEditorObject<T extends Record<string, unknown>> = {
    properties: {
        [K in keyof T]: StructurableEditor<T[K]>;
    };
    name?: NameMapper<T>;
    icon?: IconMapper<T>;
} & (
    | {
          kind: "block";
      }
    | {
          kind: "fieldset";
          layout: "grid" | "grid-2" | "rows";
          collapsedLayout?: "grid" | "grid-2" | "rows";
      }
);

export type StructurableEditor<T> =
    | StructurableEditorCommon
    | ([T] extends [boolean] ? StructurableEditorBoolean : never)
    | ([T] extends [string] ? StructurableEditorString : never)
    | ([T] extends [string[]] ? StructurableEditorStringArray : never)
    | ([T] extends [number] ? StructurableEditorNumber : never)
    | ([T] extends [(infer U)[]] ? StructurableEditorObjectArray<U> : never)
    | ([T] extends [Record<string, unknown>]
          ? StructurableEditorObject<T>
          : never);

export type EditorDefault<T> = [T] extends [unknown[]]
    ? {
          defaultValue: DefaultFunction<T>;
      }
    : [T] extends [Record<string, unknown>]
      ? {
            defaultValue: DefaultFunction<{
                [K in keyof T]: [EditorDefault<T[K]>] extends [
                    {
                        defaultValue: DefaultFunction<infer U>;
                    },
                ]
                    ? U
                    : never;
            }>;
        }
      : {
            defaultValue: DefaultFunction<T>;
        };

type Unwrap<T> = T extends Function
    ? T
    : T extends object
      ? { [K in keyof T]: T[K] } & {}
      : T;
type Unwrap2<T> = T extends Function
    ? T
    : T extends object
      ? { [K in keyof T]: Unwrap<T[K]> } & {}
      : T;
type Unwrap3<T> = T extends Function
    ? T
    : T extends object
      ? { [K in keyof T]: Unwrap2<T[K]> } & {}
      : T;

export type EditorWithMeta<
    T,
    ET extends StructurableEditor<T>,
    S extends JSONSchemaType<T>,
> = Unwrap<
    {
        meta: S;
    } & ET &
        Unwrap<EditorDefault<T>>
>;

export type EditableRecord<T, K extends string = string> = Record<
    K,
    EditorWithMeta<T, StructurableEditor<T>, JSONSchemaType<T>>
> & {};

export function visit<
    const K extends string,
    TT extends F extends DefaultFunction<infer FT> ? FT : never,
    const ET extends StructurableEditor<TT>,
    const ST extends JSONSchemaType<TT>,
    F,
>(name: K, structure: ST, editor: ET, defaultValue: F) {
    return <const M>(fields: M) =>
        ({
            ...fields,
            [name]: { ...editor, defaultValue, meta: structure },
        }) as M & {
            [P in K]: Unwrap<EditorWithMeta<TT, ET, ST>>;
        };
}

export class StructuredEditVisitor<const M extends EditableRecord<any>> {
    protected readonly fields: M;

    constructor(initial: M) {
        this.fields = { ...initial };
    }

    discriminant<const K extends keyof M, const TT extends EnumSchema<M[K]>>(
        name: K,
        value: TT,
    ) {
        return new StructuredEditVisitor({
            ...this.fields,
            [name]: {
                ...this.fields[name],
                defaultValue: (_props: DefaultProps) => value,
                meta: {
                    ...this.fields[name].meta,
                    enum: [value] as const,
                    const: value,
                },
            },
        } as Omit<M, K> & {
            [P in K]: Omit<M[K], "defaultValue"> & {
                defaultValue: DefaultFunction<TT>;
                meta: {
                    enum: readonly [TT];
                    const: TT;
                };
            };
        });
    }

    map<const F extends (fields: M) => any>(
        f: F,
    ): StructuredEditVisitor<M & ReturnType<F>> {
        return new StructuredEditVisitor(f(this.fields));
    }

    build({
        name,
        icon,
    }: {
        name?: NameMapper<EditorType<M>>;
        icon?: IconMapper<EditorType<M>>;
    }): {
        editor: {
            kind: "block";
            properties: Unwrap3<M>;
            defaultValue: DefaultFunction<EditorType<M>>;
            meta: {
                type: "object";
                properties: Unwrap<EditorSchema<M>>;
                required: Array<keyof M>;
            };
            name?: NameMapper<EditorType<M>>;
            icon?: IconMapper<EditorType<M>>;
        };
        schema: {
            type: "object";
            properties: Unwrap<EditorSchema<M>>;
            required: Array<keyof M>;
        };
    } {
        const res = this.fields as Unwrap3<M>;

        const meta = {
            type: "object",
            properties: Object.fromEntries(
                Object.entries(res).map(([k, v]) => [k, v.meta] as const),
            ) as Unwrap<EditorSchema<M>>,
            required: Object.keys(res) as (keyof M)[],
        } as const;

        return {
            editor: {
                kind: "block",
                properties: res,
                defaultValue: (props: DefaultProps) => {
                    const val = {} as Unwrap<EditorType<M>>;
                    for (const k in res) {
                        val[k] = res[k].defaultValue(props);
                    }

                    return val;
                },
                meta,
                name,
                icon,
            },
            schema: meta,
        };
    }
}

type EnumSchema<T> = [T] extends [
    {
        meta: { enum: readonly (infer E extends string)[] };
    },
]
    ? E
    : never;

type EditorSchema<T extends Record<string, any>> = {
    [Key in keyof T]: [T[Key]] extends [{ meta: infer U }] ? U : never;
};

type EditorType<T extends Record<string, any>> = {
    -readonly [Key in keyof T]: [T[Key]] extends [
        { defaultValue: DefaultFunction<infer U> },
    ]
        ? U
        : never;
};

export type EditorPublicType<T> = [T] extends [
    {
        editor: { defaultValue: DefaultFunction<infer U> };
    },
]
    ? U
    : never;
